import { useHasPermission } from "@common/hooks/use-permissions"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { Suspense } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { NewQuoteButton } from "../-components/new-quote-button"
import { prefetchQuotes, QuoteTable } from "../-components/quote-table"
import { TableSkeleton } from "../-components/task-table"
import { LIST_TABS, TemplateBar } from "../-components/template-bar"
import { QUOTE_FILTERS } from "../-lib/quote-filter-config"
import {
  FIXED_QUOTE_COLUMNS,
  hasQuoteFilters,
  NO_QUOTE_FILTERS,
  QUOTE_COLUMN_LABELS,
  QUOTE_COLUMN_SIZING,
  QUOTE_COLUMNS,
  QUOTE_GROUPING_LABELS,
  QUOTE_GROUPINGS,
  type QuoteFilters,
  quoteColumns,
  quoteColumnsSearch,
  quoteDisplay,
  quoteDisplaySearch,
  quoteFilterSchema,
  quoteSortSearch,
} from "../-lib/quote-list"

export const Route = createFileRoute("/_layout/templates/quotes")({
  staticData: { breadcrumb: "Template quotes" },
  validateSearch: (search) => quoteFilterSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps }) =>
    prefetchQuotes(queryClient, deps),
  component: TemplateQuotesPage,
})

function TemplateQuotesPage(): React.ReactElement {
  const filters = Route.useSearch()
  const navigate = useNavigate()
  const canCreate = useHasPermission("canned_work_package:create")
  const [ref, width] = useWidth<HTMLDivElement>()
  const columns = quoteColumns(filters)
  const sizing = useColumnSizing(columns, QUOTE_COLUMN_SIZING)
  const fit = fitColumns(columns, width, sizing)
  const apply = (patch: Partial<QuoteFilters>) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })

  return (
    <ListPage
      toolbar={
        <TemplateBar
          name="Template quotes"
          tabs={LIST_TABS}
          tab={filters.archived ? "archived" : "all"}
          panelOpen={false}
          onSelect={(tab) =>
            apply({ archived: tab === "archived" || undefined })
          }
        >
          <NameFilter
            search={filters.search ?? ""}
            onSearch={(search) => apply({ search: search || undefined })}
            placeholder="Filter template quotes…"
          />
          <FilterButton
            active={hasQuoteFilters(filters)}
            configs={QUOTE_FILTERS}
            onChange={apply}
          />
          <DisplayOptions
            groupings={QUOTE_GROUPINGS}
            labels={QUOTE_GROUPING_LABELS}
            {...quoteDisplay(filters)}
            onChange={(next) => apply(quoteDisplaySearch(next))}
          >
            <ColumnChips
              options={QUOTE_COLUMNS}
              labels={QUOTE_COLUMN_LABELS}
              fixed={FIXED_QUOTE_COLUMNS}
              columns={columns}
              hidden={fit.hidden}
              onChange={(next) => apply(quoteColumnsSearch(next))}
            />
          </DisplayOptions>
          {canCreate && (
            <NewQuoteButton
              onCreated={(created) =>
                navigate({
                  to: "/templates/quotes/$packageId/overview",
                  params: { packageId: created.id },
                })
              }
            />
          )}
        </TemplateBar>
      }
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilterBar
        active={hasQuoteFilters(filters)}
        configs={QUOTE_FILTERS}
        values={filters}
        onChange={apply}
        onClear={() => apply(NO_QUOTE_FILTERS)}
      />
      <Suspense fallback={<TableSkeleton />}>
        <QuoteTable
          filters={filters}
          columns={fit.shown}
          onSort={(column) => apply(quoteSortSearch(filters, column))}
          onClear={() => apply({ ...NO_QUOTE_FILTERS, search: undefined })}
        />
      </Suspense>
    </ListPage>
  )
}
